import React from "react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../lib/api";
import { trackCitationJump, trackCitationToVault } from "../lib/analytics";
import { useSourceReader } from "./SourceReader";

const STRENGTH_STYLES = {
    high: { label: "Strong evidence", className: "text-[#56CCF2] border-[#56CCF2]/40 bg-[#56CCF2]/10" },
    medium: { label: "Moderate evidence", className: "text-[#2F80ED] border-[#2F80ED]/40 bg-[#2F80ED]/10" },
    low: { label: "Limited evidence", className: "text-[#94A3B8] border-[#243044] bg-[#243044]/50" },
};

const POSITION_STYLES = {
    supporting: "Supports official view",
    critical: "Challenges official view",
    neutral: "Neutral / observational",
    debated: "Disputed",
};

function jumpToSource(idx) {
    const el = document.getElementById(`source-${idx}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#56CCF2]", "ring-offset-2", "ring-offset-[#0B1220]");
    window.setTimeout(() => {
        el.classList.remove("ring-2", "ring-[#56CCF2]", "ring-offset-2", "ring-offset-[#0B1220]");
    }, 1800);
}

export default function EvidenceItem({ item, sources = [] }) {
    const { openSource } = useSourceReader();
    if (!item) return null;
    const strength = STRENGTH_STYLES[item.strength] || null;

    const attachments = item.attachments || [];
    const refs = (item.source_refs || []).map((rid) => {
        const idx = sources.findIndex((s) => s.id === rid);
        if (idx < 0) return null;
        return { n: idx + 1, source: sources[idx] };
    }).filter(Boolean);

    return (
        <div className="space-y-2" data-testid="evidence-item">
            {item.title && (
                <div className="flex items-start gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-white flex-1">{item.title}</p>
                    {strength && (
                        <span data-testid="evidence-strength" className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${strength.className}`}>
                            {strength.label}
                        </span>
                    )}
                </div>
            )}
            {item.position && POSITION_STYLES[item.position] && (
                <p data-testid="evidence-position" className="text-[10px] uppercase tracking-wider text-[#64748B]">{POSITION_STYLES[item.position]}</p>
            )}
            {item.content && (
                <p className="text-sm text-[#94A3B8] leading-relaxed">{item.content}</p>
            )}
            {item.date && (
                <p className="text-xs text-[#64748B]">{item.date}</p>
            )}
            {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                    {attachments.map((att, i) => {
                        const url = att.url?.startsWith("http") ? att.url : `${BACKEND_URL}${att.url}`;
                        return (
                            <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#0B1220] border border-[#243044] text-[#56CCF2] hover:border-[#2F80ED] transition-colors"
                            >
                                {att.kind === "pdf" ? "PDF" : att.kind === "image" ? "Image" : "Link"}
                                {att.label ? `· ${att.label}` : ""}
                            </a>
                        );
                    })}
                </div>
            )}
            {refs.length > 0 && (
                <div data-testid="evidence-source-refs" className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[10px] uppercase tracking-wider text-[#64748B] mr-1">Sources</span>
                    {refs.map(({ n, source }, i) => {
                        const key = (source?.url || (source?.title ? `title::${source.title.toLowerCase()}` : "")).toLowerCase();
                        return (
                            <span key={i} className="inline-flex items-stretch rounded-md overflow-hidden border border-[#2F80ED]/40 hover:border-[#56CCF2] transition-colors">
                                <button
                                    type="button"
                                    data-testid={`evidence-citation-${n}`}
                                    onClick={() => { trackCitationJump(n, key); jumpToSource(n); }}
                                    title={source?.title ? `Jump to source [${n}] — ${source.title}` : `Jump to source [${n}]`}
                                    className="text-[10px] font-mono font-semibold px-2 py-0.5 bg-[#0B1220] text-[#56CCF2] hover:bg-[#2F80ED]/15 hover:text-white transition-colors"
                                >
                                    [{n}]
                                </button>
                                {key && (
                                    <Link
                                        data-testid={`evidence-citation-vault-${n}`}
                                        to={`/sources?source=${encodeURIComponent(key)}`}
                                        onClick={(e) => {
                                            trackCitationToVault(n, key);
                                            if (!source || e.metaKey || e.ctrlKey || e.shiftKey) return;
                                            e.preventDefault();
                                            openSource(source);
                                        }}
                                        title={`Open source [${n}] — ${source?.title || "in the Source Vault"}`}
                                        className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 bg-[#0B1220] text-[#64748B] hover:bg-[#2F80ED]/15 hover:text-[#56CCF2] border-l border-[#2F80ED]/30 transition-colors flex items-center gap-0.5"
                                    >
                                        Read <span aria-hidden="true">↗</span>
                                    </Link>
                                )}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
